import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Trash2, Paperclip, Check, X, Sparkles, Loader2, Plus, ClipboardList, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Shared chat surface — used by both the floating drawer and the dedicated page.
 * Renders session list, message thread, action approval cards, composer with file upload.
 */
const AdminChat = ({ compact = false }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null); // {filename, rows}
  const [uploading, setUploading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);
  const [reverting, setReverting] = useState(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 60);
  };

  const loadSessions = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin-chat/sessions`, { headers: auth() });
      if (!r.ok) return;
      const data = await r.json();
      setSessions(data);
      if (!currentSession && data.length > 0) selectSession(data[0].id);
    } catch { /* silent */ }
  }, [currentSession]);

  const selectSession = async (id) => {
    setCurrentSession(id);
    try {
      const r = await fetch(`${API}/api/admin-chat/sessions/${id}/messages`, { headers: auth() });
      if (r.ok) { setMessages(await r.json()); scrollToBottom(); }
    } catch { /* silent */ }
  };

  const newSession = async () => {
    try {
      const r = await fetch(`${API}/api/admin-chat/sessions`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'New conversation' }),
      });
      const s = await r.json();
      await loadSessions();
      setCurrentSession(s.id);
      setMessages([]);
    } catch { toast.error('Could not create session'); }
  };

  const deleteSession = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await fetch(`${API}/api/admin-chat/sessions/${id}`, { method: 'DELETE', headers: auth() });
      if (id === currentSession) { setCurrentSession(null); setMessages([]); }
      await loadSessions();
    } catch { toast.error('Delete failed'); }
  };

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/admin-chat/upload-recipients`, { method: 'POST', headers: auth(), body: fd });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || 'Upload failed'); }
      const j = await r.json();
      setPendingAttachment({ filename: j.filename, rows: j.rows });
      toast.success(`Parsed ${j.valid_rows} recipients from ${j.filename}`);
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    setUploading(false);
  };

  const send = async () => {
    if (!input.trim() && !pendingAttachment) return;
    if (!currentSession) { await newSession(); return send(); }

    const text = input.trim() || (pendingAttachment ? `Please add these ${pendingAttachment.rows.length} recipients.` : '');
    const attachments = pendingAttachment ? [{ kind: 'recipients', filename: pendingAttachment.filename, rows: pendingAttachment.rows }] : null;
    const provisional = { id: `tmp-${Date.now()}`, role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages(m => [...m, provisional]);
    setInput('');
    setPendingAttachment(null);
    setSending(true);
    scrollToBottom();

    try {
      const r = await fetch(`${API}/api/admin-chat/message`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: currentSession, text, attachments }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || 'Failed'); }
      const reply = await r.json();
      // Reload full history for accuracy (also refreshes action state)
      const hist = await fetch(`${API}/api/admin-chat/sessions/${currentSession}/messages`, { headers: auth() });
      if (hist.ok) setMessages(await hist.json());
      scrollToBottom();
    } catch (e) {
      toast.error(e.message || 'Assistant unavailable');
      setMessages(m => m.filter(x => x.id !== provisional.id));
    }
    setSending(false);
  };

  const applyAction = async (actionId) => {
    try {
      const r = await fetch(`${API}/api/admin-chat/actions/${actionId}/apply`, { method: 'POST', headers: auth() });
      const j = await r.json();
      if (r.ok && j.ok) toast.success('Applied'); else toast.error(j.result?.error || 'Apply failed');
      const hist = await fetch(`${API}/api/admin-chat/sessions/${currentSession}/messages`, { headers: auth() });
      if (hist.ok) setMessages(await hist.json());
    } catch { toast.error('Apply failed'); }
  };

  const rejectAction = async (actionId) => {
    try {
      await fetch(`${API}/api/admin-chat/actions/${actionId}/reject`, { method: 'POST', headers: auth() });
      const hist = await fetch(`${API}/api/admin-chat/sessions/${currentSession}/messages`, { headers: auth() });
      if (hist.ok) setMessages(await hist.json());
    } catch { toast.error('Reject failed'); }
  };

  const loadAudit = async () => {
    try {
      const r = await fetch(`${API}/api/admin-chat/audit?limit=50`, { headers: auth() });
      if (r.ok) setAudit(await r.json());
    } catch { toast.error('Failed to load audit log'); }
  };

  const revertAudit = async (id) => {
    if (!window.confirm('Revert this change? The affected fields will be restored to their previous values.')) return;
    setReverting(id);
    try {
      const r = await fetch(`${API}/api/admin-chat/audit/${id}/revert`, { method: 'POST', headers: auth() });
      const j = await r.json();
      if (r.ok && j.ok) toast.success('Reverted'); else toast.error(j.detail || 'Revert failed');
      await loadAudit();
    } catch { toast.error('Revert failed'); }
    setReverting(null);
  };

  useEffect(() => { if (showAudit) loadAudit(); }, [showAudit]);

  return (
    <div className={`admin-chat ${compact ? 'compact' : ''}`} data-testid="admin-chat-root">
      {!compact && (
        <aside className="admin-chat-sidebar">
          <button onClick={newSession} className="admin-chat-new-session-btn" data-testid="new-session-btn">
            <Plus className="w-3.5 h-3.5" /> New conversation
          </button>
          <div className="admin-chat-sessions">
            {sessions.length === 0 && <p className="admin-chat-empty">No conversations yet</p>}
            {sessions.map(s => (
              <div key={s.id} onClick={() => selectSession(s.id)}
                className={`admin-chat-session-row ${s.id === currentSession ? 'active' : ''}`}
                data-testid={`session-${s.id}`}>
                <span className="truncate flex-1">{s.title}</span>
                <button onClick={(e) => deleteSession(s.id, e)} className="admin-chat-session-del"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </aside>
      )}

      <div className="admin-chat-main">
        <div className="admin-chat-topbar">
          <button
            onClick={() => setShowAudit(v => !v)}
            className={`admin-chat-audit-toggle ${showAudit ? 'active' : ''}`}
            data-testid="audit-toggle-btn"
            title="Toggle audit log"
          >
            <ClipboardList className="w-3.5 h-3.5" />
            {showAudit ? 'Chat' : 'Audit'}
          </button>
        </div>

        {showAudit && (
          <div className="admin-chat-audit-panel" data-testid="audit-panel">
            {audit.length === 0 && <p className="admin-chat-empty">No audit entries yet.</p>}
            {audit.map(a => (
              <div key={a.id} className={`admin-chat-audit-row status-${a.status} ${a.reverted ? 'reverted' : ''}`}>
                <div className="admin-chat-audit-row-header">
                  <span className="admin-chat-audit-tool">{a.tool}</span>
                  <span className="admin-chat-audit-time">{new Date(a.timestamp).toLocaleString()}</span>
                </div>
                <div className="admin-chat-audit-notes">{a.notes || a.tool}</div>
                <div className="admin-chat-audit-meta">
                  <span>{a.admin}</span>
                  {a.status !== 'applied' && <span className={`admin-chat-audit-status status-${a.status}`}>{a.status}</span>}
                  {a.reverted && <span className="admin-chat-audit-status reverted">reverted</span>}
                  {a.revertible && !a.reverted && (
                    <button
                      onClick={() => revertAudit(a.id)}
                      disabled={reverting === a.id}
                      className="admin-chat-audit-revert-btn"
                      data-testid={`revert-audit-${a.id}`}
                    >
                      <Undo2 className="w-3 h-3" /> {reverting === a.id ? 'Reverting…' : 'Revert'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!showAudit && !currentSession && (
          <div className="admin-chat-welcome">
            <Sparkles className="w-8 h-8 text-emerald-500 mb-3" />
            <h3 className="text-base font-semibold">WNC Admin Assistant</h3>
            <p className="text-sm text-gray-500 mt-1 max-w-md">
              Ask me to edit homepage text, draft an email campaign, or add recipients from a CSV. Every change requires your approval before it goes live.
            </p>
            <button onClick={newSession} className="admin-chat-cta">Start a conversation</button>
          </div>
        )}

        {!showAudit && currentSession && (
          <>
            <div ref={scrollRef} className="admin-chat-thread" data-testid="chat-thread">
              {messages.map(m => (
                <div key={m.id} className={`admin-chat-msg ${m.role}`} data-testid={`msg-${m.role}`}>
                  <div className="admin-chat-msg-role">{m.role === 'user' ? 'You' : 'Assistant'}</div>
                  <div className="admin-chat-msg-body">{m.content || '…'}</div>
                  {m.pending_action && (
                    <div className={`admin-chat-action-card status-${m.pending_action.status}`}>
                      <div className="admin-chat-action-header">
                        <span className="admin-chat-action-tool">{m.pending_action.tool}</span>
                        <span className={`admin-chat-action-status status-${m.pending_action.status}`}>{m.pending_action.status}</span>
                      </div>
                      <div className="admin-chat-action-summary">{m.pending_action.summary}</div>
                      <details className="admin-chat-action-details">
                        <summary>View details</summary>
                        <pre>{JSON.stringify(m.pending_action.args, null, 2)}</pre>
                      </details>
                      {m.pending_action.status === 'pending' && (
                        <div className="admin-chat-action-btns">
                          <button onClick={() => rejectAction(m.pending_action.id)} className="admin-chat-btn-reject" data-testid={`reject-${m.pending_action.id}`}><X className="w-3 h-3" /> Reject</button>
                          <button onClick={() => applyAction(m.pending_action.id)} className="admin-chat-btn-apply" data-testid={`apply-${m.pending_action.id}`}><Check className="w-3 h-3" /> Apply</button>
                        </div>
                      )}
                      {m.pending_action.status === 'applied' && m.pending_action.result && (
                        <div className="admin-chat-action-result success">✓ Applied · {m.pending_action.result.campaign_id || m.pending_action.result.section_id || 'saved'}</div>
                      )}
                      {m.pending_action.status === 'failed' && (
                        <div className="admin-chat-action-result error">✗ {m.pending_action.result?.error || 'Failed'}</div>
                      )}
                      {m.pending_action.status === 'rejected' && <div className="admin-chat-action-result muted">Rejected</div>}
                    </div>
                  )}
                </div>
              ))}
              {sending && <div className="admin-chat-typing"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Assistant is thinking…</div>}
            </div>

            <div className="admin-chat-composer">
              {pendingAttachment && (
                <div className="admin-chat-attachment-chip">
                  <Paperclip className="w-3 h-3" />
                  <span>{pendingAttachment.filename} · {pendingAttachment.rows.length} recipients</span>
                  <button onClick={() => setPendingAttachment(null)}><X className="w-3 h-3" /></button>
                </div>
              )}
              <div className="admin-chat-input-row">
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="admin-chat-attach-btn" title="Attach CSV or Excel" data-testid="attach-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                </button>
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xlsm,.txt" className="hidden" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Ask me to draft an email, edit homepage text, upload a recipient list…"
                  rows={1}
                  disabled={sending}
                  className="admin-chat-textarea"
                  data-testid="chat-input"
                />
                <button onClick={send} disabled={sending || (!input.trim() && !pendingAttachment)} className="admin-chat-send" data-testid="send-btn">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminChat;
